import BaseSetup from '@/components/BaseSetup';
import type OrganizationUserEntity from '@/entity/organization-user/OrganizationUserEntity';
import OrganizationUserListItemEntity from '@/entity/organization-user/OrganizationUserListItemEntity';
import useMembershipService from '@/services/MembershipService';
import { PERMISSION_ACTION } from '@/typings/enums/PERMISSION_ACTION';
import { Reactive } from '@/utils/decorators';
import { useNotif } from '@orion.ui/orion';
import { ref } from 'vue';

export type SharedCompanyFlatSelectEmits = {
	(e: 'update:opened', val: boolean): void
	(e: 'update:modelValue', val?: ICompanyModel[]): void
	(e: 'add' | 'delete', val?: ICompanyModel): void
};
export type SharedCompanyFlatSelectProps = {
	displayAddAll?: boolean
	opened?: boolean
	modelValue?: ICompanyWithMembershipModel[]
	user?: OrganizationUserListItemEntity | OrganizationUserEntity
	placement?: string
};

export default class SharedCompanyFlatSelectSetup extends BaseSetup {

	static readonly defaultProps = { placement: 'bottom-start' };

	readonly _popover = ref<Dropdown>();

	@Reactive private readonly state = {
		loading: false,
		search: undefined as Undef<string>,
		companies: [] as ICompanyModel[],
	};

	get loading () { return this.state.loading }
	get companies () { return this.state.companies as ICompanyModel[] }
	get companiesList () {
		return this.companies
			.filter(x => !this.search?.trim().length || x.name?.toLowerCase().includes(this.search))
			.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
	}

	get companiesListOptionsForFlatSelect (): FlatSelectOptionItem[] {
		return this.companiesList.map(x => ({
			...x,
			name: x.name ?? '',
		}));
	}

	get search () { return this.state.search }
	set search (val) { this.state.search = val }

	constructor (
		private props: SharedCompanyFlatSelectProps,
		private emits: SharedCompanyFlatSelectEmits,
	) {
		super();
	}

	async handlePopoverShowAsync () {
		this.emits('update:opened', true);
		await this.getCompaniesAsync();
	}

	handlePopoverHide () {
		this.emits('update:opened', false);
	}

	companyIsSelected (company: ICompanyForDetailOrganizationClientUserModel) {
		return !!this.props.modelValue
			?.filter(x => !!x.membershipId)
			.mapKey('id')
			.includes(company.id);
	}

	private async getCompaniesAsync () {
		this.state.loading = true;

		const permissionAction: OmitUserId<ICompanyListByUserIdParameters> = {
			checkOrderPlugin: false,
			permissionAction: PERMISSION_ACTION.UserRead,
		};

		const companies = await useMembershipService().listAsync(false, {
			...permissionAction,
			organizationId: this.self.organization?.id,
		});
		this.state.companies = companies;

		this.state.loading = false;
	}

	async addCompanyAsync (company: ICompanyModel) {
		if (!this.props.user?.userId || !this.organizationGuard()) return;

		try {
			const res = await useMembershipService().addAsync({
				companyIds: [company.id],
				userId: this.props.user.userId,
				organizationId: this.self.organization.id,
			});

			if (!res) return;

			useNotif.success(this.$t('common.modificationSaved'), this.$t('business.companyLinkAdded', { name: company.name }));

			this.emits('update:modelValue', [
				...(this.props.modelValue ?? []),
				{
					...company,
					membershipId: res[0].id,
				},
			]);
			this.emits('add', company);
		}
		catch (e: any) {
			useNotif.danger(e?.response?.data);
		}
	}

	async addAllCompaniesAsync () {
		if (!this.props.user?.userId || !this.organizationGuard()) return;

		try {
			const res = await useMembershipService().addAsync({
				companyIds: this.props.modelValue?.filter(x => !x.membershipId).mapKey('id') ?? [],
				userId: this.props.user.userId,
				organizationId: this.self.organization.id,
			});

			if (!res) return;

			useNotif.success(this.$t('common.modificationSaved'), this.$t('business.allCompaniesLinked'));

			this.emits('update:modelValue', [
				...this.companiesList.map(x => ({
					...x,
					membershipId: res.findByKey(x.id, 'companyId'),
				})),
			]);
			this.emits('add');
		}
		catch (e: any) {
			useNotif.danger(e?.response?.data);
		}
	}

	async deleteCompanyAsync (company: ICompanyModel) {
		const membershipIdToDelete = this.props.modelValue?.filter(x => x.id === company.id)?.[0].membershipId as number;
		await this.props.user?.deleteMembershipAsync(membershipIdToDelete);

		useNotif.success(this.$t('common.modificationSaved'), this.$t('business.companyUnlinked'));
		this.emits('update:modelValue', this.props.modelValue?.filter(x => x.id !== company.id));
		this.emits('delete', company);
	}

}
